import { FC, PropsWithChildren } from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { usePrevNextButtons } from '@/components/everridge/carousel/ECarouselButtons';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import ArrowRight_01_SharpIcon from '@/components/icons/ArrowRight_01_SharpIcon';
import ArrowLeft_01_SharpIcon from '@/components/icons/ArrowLeft_01_SharpIcon';

const OPTIONS: EmblaOptionsType = {
	align: 'start',
	dragFree: false,
	loop: false,
};

type Props = {
	options?: EmblaOptionsType;
	containerClassName?: string;
	buttonsContainerClassName?: string;
	buttonsPosition?: 'sides' | 'bottom';
	buttonsSize?: ButtonProps['size'];
} & PropsWithChildren;

const ECarousel: FC<Props> = ({
	options = OPTIONS,
	children,
	containerClassName,
	buttonsContainerClassName,
	buttonsPosition = 'sides',
	buttonsSize = 'sm',
}) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);

	const {
		prevBtnDisabled,
		nextBtnDisabled,
		onPrevButtonClick,
		onNextButtonClick,
	} = usePrevNextButtons(emblaApi);

	return (
		<div className='relative flex flex-col items-center justify-center gap-6'>
			<div
				className='h-full w-full overflow-hidden'
				ref={emblaRef}>
				<div
					className={cn(
						'h-full flex touch-pan-y touch-pinch-zoom',
						containerClassName,
					)}>
					{children}
				</div>
			</div>
			<div
				className={cn(
					buttonsPosition === 'sides'
						? 'absolute inset-0 flex items-center justify-between px-4 pointer-events-none'
						: 'flex justify-center gap-4',
					buttonsContainerClassName,
				)}>
				<Button
					variant='outline'
					size={buttonsSize}
					disabled={prevBtnDisabled}
					onClick={onPrevButtonClick}
					className={cn(
						'pointer-events-auto transition-opacity',
						prevBtnDisabled
							? 'opacity-0 pointer-events-none'
							: 'opacity-100',
					)}>
					<ArrowLeft_01_SharpIcon />
				</Button>
				<Button
					variant='outline'
					size={buttonsSize}
					disabled={nextBtnDisabled}
					onClick={onNextButtonClick}
					className={cn(
						'pointer-events-auto transition-opacity',
						nextBtnDisabled
							? 'opacity-0 pointer-events-none'
							: 'opacity-100',
					)}>
					<ArrowRight_01_SharpIcon />
				</Button>
			</div>
		</div>
	);
};

export default ECarousel;
